<template lang="pug">
FullCalendar(
  ref="fullCalendar"
  :options="calendarOptions"
)
  template(#dayHeaderContent="{ date }")
    .mp-day-head
      .mp-day-week {{ dayName(date) }}
      .mp-day-number {{ dayNumber(date) }}
  template(#eventContent="{ event }")
    .fc-content(v-if="event.allDay")
      span.fc-title.ellipsis {{ event.title }}
      span.event-duration All day
    MemberPanelEventPopover(
      v-else-if="event.extendedProps.type === 'time_entry'"
      :event="event"
      :time-zone="timeZone"
      :prevent-own-time-deletion="preventOwnTimeDeletion"
    )
    .fc-content(v-else)
      .fc-time
        span {{ formattedRange(event) }}
      .fc-title
        | {{ event.title }}
      .fc-task(v-if="event.extendedProps.task") {{ event.extendedProps.task }}
</template>
<script>
import FullCalendar from '@fullcalendar/vue3';
import timeGridPlugin from '@fullcalendar/timegrid';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import interactionPlugin from '@fullcalendar/interaction';
import { formatDatetimeRange } from '~/utils/time_utils';
import MemberPanelEventPopover from './MemberPanelEventPopover.vue';

const ResizeObserver = window.ResizeObserver;
const HOUR_TIME_FORMAT_24 = { hour: 'numeric', minute: '2-digit', hour12: false };
// A per-week grid embedded in the scrolling list of panels needs a fixed height, not the
// viewport-fill sizing the full-page legacy calendar uses.
const CALENDAR_HEIGHT = 600;
// Add/edit/delete reload the panel frame, which remounts this component and snaps the grid back to
// scrollTime. Stash each panel's timed-grid scroll position at module scope (so it survives the remount)
// keyed by the panel wrapper's id — that element persists while only the inner frame reloads — and
// restore it on the fresh instance.
const savedScrollTops = new Map();
// The vertically-scrolling timed body (present because the grid vGrows with a fixed height).
const TIMED_SCROLLER = '.fc-scroller-liquid-absolute';

export default {
  name: 'MemberPanelCalendar',
  components: {
    FullCalendar,
    MemberPanelEventPopover,
  },
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    timeZone: {
      type: String,
      default: '',
    },
    firstDay: {
      type: Number,
      default: 1,
    },
    defaultDate: {
      type: String,
      default: '',
    },
    stopDate: {
      type: String,
      default: '',
    },
    preventOwnTimeDeletion: {
      type: Boolean,
      default: false,
    },
    batchTimeEntryPath: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      resizeObserver: null,
      scrollTime: '08:00:00',
    };
  },
  computed: {
    timeFormat() {
      return /24/.test(TimeUtils.UserTimeFormats.time) ? HOUR_TIME_FORMAT_24 : null;
    },
    calendarOptions() {
      return {
        initialView: 'timeGridWeek',
        initialDate: this.defaultDate,
        timeZone: this.timeZone,
        firstDay: this.firstDay,
        plugins: [timeGridPlugin, momentTimezonePlugin, interactionPlugin],
        events: this.events,
        height: CALENDAR_HEIGHT,
        allDayText: '',
        allDaySlot: true,
        editable: false,
        selectable: Boolean(this.batchTimeEntryPath),
        selectMirror: true,
        select: this.onSelect,
        selectAllow: this.selectAllow,
        // Keep the 30-min selection highlight on the grid while the "Add time" modal is open — don't let
        // any click clear it (a project/task dropdown, for instance, teleports its options outside the
        // modal). It's cleared explicitly instead: on cancel/close (the modal:closed handler below) and on
        // a successful save (the panel reloads and the grid remounts).
        unselectAuto: false,
        nowIndicator: true,
        slotEventOverlap: false,
        eventMinHeight: 20,
        headerToolbar: false,
        stickyHeaderDates: false,
        scrollTime: this.scrollTime,
        slotLabelFormat: this.timeFormat,
        eventTimeFormat: this.timeFormat,
        eventClassNames: this.eventClassNames,
      };
    },
  },
  created() {
    this.setScrollTime();
  },
  mounted() {
    this.scrollKey = this.$el.closest('turbo-frame')?.closest('[x-data]')?.id || null;
    this.scrollRestored = false;
    this.onGridScroll = (event) => {
      if (this.scrollKey && event.target?.classList?.contains('fc-scroller-liquid-absolute')) {
        savedScrollTops.set(this.scrollKey, event.target.scrollTop);
      }
    };
    this.$el.addEventListener('scroll', this.onGridScroll, true);
    // Drop the leftover selection highlight when the "Add time" modal is dismissed without saving
    // (zoneModal bubbles modal:closed on X/Esc/backdrop; a save reloads the panel and remounts instead).
    this.onModalClosed = () => this.$refs.fullCalendar?.getApi().unselect();
    document.addEventListener('modal:closed', this.onModalClosed);
    this.setupResizeObserver();
    this.setupAddTimeRefresh();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.onGridScroll) {
      this.$el.removeEventListener('scroll', this.onGridScroll, true);
    }
    if (this.onModalClosed) {
      document.removeEventListener('modal:closed', this.onModalClosed);
    }
    if (this.onBatchSubmitStart) {
      document.removeEventListener('turbo:submit-start', this.onBatchSubmitStart);
    }
  },
  methods: {
    formattedRange(event) {
      return formatDatetimeRange(this.inZone(event.start), this.inZone(event.end), { style: 'time' });
    },
    inZone(date) {
      return this.timeZone ? moment(date).tz(this.timeZone) : moment(date);
    },
    // Selecting empty grid space opens the batch "Add time" modal pre-filled with the slot (a click
    // gives a 30-min snap, a drag gives the dragged span). Mirrors TimeEntriesTimeline.addTime: the
    // dialog is the persistent turbo-frame#modal, which opens when navigated to the batch `new` URL.
    onSelect(info) {
      const frame = document.querySelector('turbo-frame#modal');

      if (!frame || !this.batchTimeEntryPath) {
        return;
      }

      const start = this.inZone(info.start);
      const stop = this.inZone(info.end);
      const separator = this.batchTimeEntryPath.includes('?') ? '&' : '?';
      const params = new URLSearchParams({
        'batch_time_entry[time_spans][][date]': start.format('YYYY-MM-DD'),
        'batch_time_entry[time_spans][][from]': start.format('HH:mm'),
        'batch_time_entry[time_spans][][to]': stop.format('HH:mm'),
      });

      frame.src = `${this.batchTimeEntryPath}${separator}${params}`;
    },
    // Only allow adding time in the past, in selections of at most one day. Future days/slots can't be
    // selected (the whole selection must end at or before now). The one-day cap matters because onSelect
    // serializes a selection to a single date + from/to, which the backend can stretch by at most one day
    // — so a multi-day drag would be silently truncated. info.start/end are real instants.
    selectAllow(info) {
      return info.end - info.start <= moment.duration(1, 'day').asMilliseconds() && info.end <= new Date();
    },
    dayName(date) {
      return this.inZone(date).format('ddd').toUpperCase();
    },
    dayNumber(date) {
      return this.inZone(date).format('D');
    },
    eventClassNames(info) {
      const classNames = [`fc-event-${info.event.extendedProps.type || 'time_entry'}`];

      if (info.event.allDay) {
        classNames.push('fc-all-day');
      } else {
        const duration = moment(info.event.end) - moment(info.event.start);

        if (duration < moment.duration(1, 'hour').asMilliseconds()) {
          classNames.push('fc-short');
        }
      }

      return classNames;
    },
    // Land the initial scroll on the earliest clock time any event starts at during the week, so no
    // event sits above the initial viewport. Compare time-of-day, not the full datetime — otherwise a
    // late event on an earlier day would beat an early event on a later day. Falls back to the default
    // work-day start when there are no timed events.
    setScrollTime() {
      const minutesOfDay = this.events
        .filter((event) => !event.allDay && event.start)
        .map((event) => {
          const start = this.inZone(event.start);

          return start.hours() * 60 + start.minutes();
        });

      if (!minutesOfDay.length) {
        return;
      }

      const startMinutes = Math.max(0, Math.min(...minutesOfDay) - 30);

      this.scrollTime = moment({ hour: Math.floor(startMinutes / 60), minute: startMinutes % 60 }).format('HH:mm:ss');
    },
    // Whether a submitted add-time date falls inside this panel's loaded range — used to refresh only the
    // panel the entry belongs to (the page's week panels don't overlap, so at most one matches).
    coversDate(dateStr) {
      const date = moment(dateStr);

      return date.isSameOrAfter(moment(this.defaultDate), 'day') && date.isSameOrBefore(moment(this.stopDate), 'day');
    },
    // After the batch "Add time" create succeeds, refresh just this panel in place — the same update the
    // edit/split/delete dialogs do via panel.loadData().
    setupAddTimeRefresh() {
      const panel = this.$el.closest('turbo-frame')?.closest('[x-data]')?._x_dataStack?.[0];

      if (!panel?.loadData) {
        return;
      }

      this.onBatchSubmitStart = (event) => {
        // Only the batch add-time form carries time-span dates; several week panels each run this calendar
        // and listen on the document, so only refresh the one whose range covers an added date.
        const dates = new FormData(event.target).getAll('batch_time_entry[time_spans][][date]');

        if (!dates.some((date) => this.coversDate(date))) {
          return;
        }

        const refreshPanel = (streamEvent) => {
          const stream = streamEvent.target;

          // The create response closes the modal (update → #modal) only on success; a validation error
          // replaces it with the re-rendered form instead, so this skips failed submits.
          if (stream.getAttribute('action') !== 'update' || stream.getAttribute('target') !== 'modal') {
            return;
          }

          document.removeEventListener('turbo:before-stream-render', refreshPanel);
          panel.loadData();
        };

        document.addEventListener('turbo:before-stream-render', refreshPanel);
        // Safety: drop it if the submit didn't close the modal (validation error, background batch).
        setTimeout(() => document.removeEventListener('turbo:before-stream-render', refreshPanel), 3000);
      };

      document.addEventListener('turbo:submit-start', this.onBatchSubmitStart);
    },
    // The grid mounts while its Alpine `x-show` container is display:none, where FullCalendar renders
    // at zero height. When the user switches to the calendar view the container gains size and the
    // observer fires, so we recompute the layout then (and on any later panel-width change).
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.$el.offsetWidth > 0 && this.$el.offsetHeight > 0) {
          this.$refs.fullCalendar?.getApi().updateSize();
          this.restoreScroll();
        }
      });
      this.resizeObserver.observe(this.$el);
    },
    // Restore this panel's saved scroll once, after updateSize has laid the grid out (so the scrollTop
    // sticks and overrides the scrollTime the fresh instance started at). Runs only when there's a saved
    // position — i.e. after a reload, not on the first mount.
    restoreScroll() {
      if (this.scrollRestored || !this.scrollKey || !savedScrollTops.has(this.scrollKey)) {
        return;
      }

      const scroller = this.$el.querySelector(TIMED_SCROLLER);

      if (scroller) {
        scroller.scrollTop = savedScrollTops.get(this.scrollKey);
        this.scrollRestored = true;
      }
    },
  },
};
</script>
<style>
.member-panel-calendar {
  .fc-scrollgrid-section {
    height: auto;
  }

  .fc-cell-shaded {
    padding: 0;
  }

  .fc-scrollgrid-section-header .fc-scroller{
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    overflow: hidden !important;
    border-bottom: 1px solid #e5e7eb;
  }

  .fc-scrollgrid-section-header th {
    border-bottom: none;
  }

  .fc-scrollgrid-section-header th:last-child {
    border-right: none;
  }

  .fc-scrollgrid-section-header + .fc-scrollgrid-section-body .fc-scroller,
  .fc-scrollgrid-section-body:first-child .fc-scroller{
    overflow: hidden !important;
  }

  .fc-timegrid.fc-view   {
    border: 1px solid #e5e7eb;
    border-radius: 8px 8px 0 8px;
  }

  .fc-timegrid::after{
    content: '';
    background: #fafafa;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    position: absolute;
    z-index: -1;
    width: 79px;
    border-radius: 8px 0 0 8px;
  }

  .fc-timegrid-axis {
    border-right-color:  #e5e7eb;
  }

  .fc-timegrid tbody tr:first-child .fc-timegrid-axis-frame.fc-scrollgrid-shrink-frame {
    padding: 0;
  }

  .fc-timegrid .fc-timegrid-slots tbody tr:nth-child(2n) td:nth-child(2) {
    border-color:  #f3f4f6;
  }

  .fc-timegrid .fc-timegrid-slots td.fc-timegrid-slot  {
    cursor: default;
  }

  .fc-timegrid-slots .fc-timegrid-slot-label-cushion  {
    color: #1f2937;
    font-size: 12px;
  }

  .fc-theme-standard td.fc-daygrid-day  {
    border-right: 1px solid #e5e7eb;
  }

  .fc-timegrid-col {
    border-color: #e5e7eb;
  }

  .fc-scrollgrid-section-body > td {
    border: none;
  }

  .fc-timegrid-col-events {
    margin: 0 1px;
  }

  .fc-daygrid-event {
    .fc-content {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .fc-title {
      flex: 1 1 auto;
      min-width: 0; /* required so the flex child can shrink and ellipsis kicks in */
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .event-duration {
      flex: 0 0 auto;
    }
  }

  .fc-daygrid-body .fc-daygrid-day-frame,
  .fc-daygrid-body .fc-daygrid-day-events {
    margin: 0;
    min-height: 0;
  }

  table.fc-scrollgrid .fc-daygrid-day { cursor: default; }

  .fc-daygrid-day-events .fc-event {
    text-shadow: none;
    cursor: default;
    .fc-content {color: #000; }
  }

  .fc-col-header-cell.fc-day {
    border-bottom: none;
  }

  .fc-event  {
    text-shadow: 1px 1px 3px rgba(0,0,0,0.3);
    overflow: hidden;
    cursor: pointer;

    .fc-content { padding: 1px 2px 0 2px; max-width: 100%; }
    .fc-time { margin: 0; font-width: normal; }
    .fc-task { margin-top: 9px; }
  }

  .fc-event-time_entry {
    display: flex;
    flex-direction: column;
  }

  .fc-event-time_entry .fc-event-main,
  .fc-event-time_entry .fc-event-main > *,
  .fc-event-time_entry .popover-container {
    display: flex !important;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }

  .fc-event-time_entry .popover-container > span {
    flex: 1 1 auto;
    min-height: 0;
  }

  .fc-timegrid-event-harness:hover {
    z-index: 999 !important;

    .fc-timegrid-event {
      overflow: visible;
      min-height: calc(100% - 1px);
      bottom: auto !important;
      opacity: 0.97;
    }
  }

  .fc-day-today {
    .mp-day-week {
      color: #0168dd;
    }
    .mp-day-number{
      background: #0168dd;
      border-radius: 30px;
      color: #fff;
      min-width: 26px;
      height: 26px;
      line-height: 26px;
      text-align: center;
    }
  }

  .mp-day-head {
    text-align: center;
    padding: 6px 0;
  }

  .mp-day-week {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.05em;
    color: #9ca3af;
    margin: 0 0 2px 0;
  }

  .mp-day-number{
    font-size: 16px;
    font-weight: 500;
    color: #374151;
  }
}
</style>
